import { HTTPError } from "ky";
import { useCallback, useEffect } from "react";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
  useFetcher,
  useLoaderData,
  useNavigate,
} from "react-router-dom";
import { fetchOrderItem, markAsMigrated } from "@/api/order-item";
import { fetchOrder } from "@/api/order";
import { Dialog } from "@/components/dialog";
import { SpinnerIcon } from "@/components/icons/spinner";
import SecondaryButton from "@/components/secondary-button";
import SubmitButton from "@/components/submit-button";
import { assertParameterExists } from "@/helpers/loader-guards";
import { OrderItemStatus } from "@/entities/order-item-status";
import { OrderItem } from "@/entities/order-item";
import { Order } from "@/entities/order";

const ELIGIBLE_STATUSES = [OrderItemStatus.New, OrderItemStatus.Setup];

interface LoaderData {
  orderItem: OrderItem;
  order: Order;
  invoiceIsUnsent: boolean;
}

export async function loader({ params }: LoaderFunctionArgs) {
  assertParameterExists(params.orderItem);

  const orderItem = await fetchOrderItem(params.orderItem);
  const order = await fetchOrder(orderItem.orderId);

  if (!ELIGIBLE_STATUSES.includes(orderItem.status)) {
    return redirect(`/order-items/${orderItem.id}`);
  }

  const invoice = [...order.invoices]
    .reverse()
    .find((invoice) =>
      invoice.items.some((item) => item.orderItemId === orderItem.id),
    );

  const unsentStatuses = ["new", "confirm_billing_details", "pending_information", "ready_to_send"];
  const invoiceIsUnsent = invoice ? unsentStatuses.includes(invoice.status) : false;

  return {
    orderItem,
    order,
    invoiceIsUnsent,
  };
}

export async function action({ params }: ActionFunctionArgs) {
  assertParameterExists(params.orderItem);

  try {
    await markAsMigrated(params.orderItem);
    return { status: 200 };
  } catch (e) {
    if (e instanceof HTTPError && e.response.status === 422) {
      const errorJson = await e.response.json();
      return {
        status: 422,
        message: errorJson.message ?? "This order item cannot be marked as migrated.",
      };
    }
    return { status: 500 };
  }
}

export default function MarkAsMigratedDialog() {
  const { orderItem, order, invoiceIsUnsent } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const fetcher = useFetcher();

  const close = useCallback(() => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate(`/order-items/${orderItem.id}`);
    }
  }, [navigate, orderItem.id]);

  type ActionData = { status?: number; message?: string } | undefined;
  const actionData = fetcher.data as ActionData;
  const isSubmitting = fetcher.state === "submitting";
  const isSuccess = actionData?.status === 200;

  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        navigate(`/order-items/${orderItem.id}`);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, navigate, orderItem.id]);

  return (
    <Dialog
      title="Mark as Migrated"
      isOpen
      onClose={close}
      description={`Mark order item #${orderItem.numericId} as migrated to the new system.`}
    >
      <fetcher.Form method="post" className="flex flex-col gap-y-4">
        <div className="p-4 bg-slate-50 rounded-lg">
          <p className="text-sm text-slate-600">
            <strong>Order Item:</strong> {orderItem.product?.title ?? "Unknown"} - {orderItem.title}
          </p>
          <p className="text-sm text-slate-600">
            <strong>Amount:</strong> {orderItem.amount.formatted}
          </p>
          <p className="text-sm text-slate-600">
            <strong>Company:</strong> {order.company?.name ?? "Unknown"}
          </p>
        </div>

        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-medium text-amber-800 mb-1">This action will:</p>
          <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
            <li>Set the order item status to <strong>Migrated</strong></li>
            <li>Invalidate the associated token</li>
            {invoiceIsUnsent ? (
              <li>Void or adjust the unsent invoice</li>
            ) : (
              <li className="text-slate-500">The associated invoice has already been sent and will not be affected</li>
            )}
          </ul>
          <p className="text-sm text-amber-800 font-medium mt-2">This action cannot be undone.</p>
        </div>

        {actionData?.status === 422 && (
          <p className="text-sm text-red-700 font-medium">{actionData.message}</p>
        )}

        {actionData?.status === 500 && (
          <p className="text-sm text-red-700 font-medium">
            An unexpected error occurred. Please try again.
          </p>
        )}

        {isSuccess && (
          <p className="rounded-lg bg-green-100 text-green-800 py-3 px-4 text-center font-medium">
            Successfully marked as migrated! Redirecting...
          </p>
        )}

        <div className="mt-4 flex items-center justify-end gap-4">
          <SecondaryButton type="button" onClick={close} size="lg" disabled={isSubmitting || isSuccess}>
            Cancel
          </SecondaryButton>
          <SubmitButton
            label="Mark as Migrated"
            size="lg"
            disabled={isSuccess}
            labelWhenSubmitting={<SpinnerIcon className="animate-spin h-4 w-4 text-white" />}
          />
        </div>
      </fetcher.Form>
    </Dialog>
  );
}
